import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { formatDateTime } from '../utils/dateFormat';

function ChannelBadge({ channel }) {
  const map = {
    email: 'bg-blue-50 text-blue-700',
    sms: 'bg-purple-50 text-purple-700',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${map[channel] || 'bg-secondary-100 text-secondary-600'}`}>
      {channel}
    </span>
  );
}

function StatusBadge({ status }) {
  const map = {
    sent: 'bg-blue-50 text-blue-700',
    failed: 'bg-red-50 text-red-700',
    delivered: 'bg-emerald-50 text-emerald-700',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${map[status] || 'bg-secondary-100 text-secondary-600'}`}>
      {status}
    </span>
  );
}

function OutcomeBadge({ outcome }) {
  const map = {
    notified: 'bg-emerald-50 text-emerald-700',
    checkin_clear: 'bg-emerald-50 text-emerald-700',
    no_contact: 'bg-amber-50 text-amber-700',
    error: 'bg-red-50 text-red-700',
    emergency: 'bg-red-50 text-red-700',
    short_press: 'bg-blue-50 text-blue-700',
    long_press: 'bg-purple-50 text-purple-700',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${map[outcome] || 'bg-secondary-100 text-secondary-600'}`}>
      {outcome?.replace(/_/g, ' ') || '--'}
    </span>
  );
}

// Disposition of an inbound SMS (inbound_sms_log.action)
function SmsActionBadge({ action }) {
  const map = {
    checkin_reset: 'bg-emerald-50 text-emerald-700',
    checkin_duplicate: 'bg-blue-50 text-blue-700',
    opt_out: 'bg-red-50 text-red-700',
    opt_in: 'bg-purple-50 text-purple-700',
    error: 'bg-red-50 text-red-700',
    no_pending: 'bg-amber-50 text-amber-700',
    no_match: 'bg-amber-50 text-amber-700',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${map[action] || 'bg-secondary-100 text-secondary-600'}`}>
      {action?.replace(/_/g, ' ') || '--'}
    </span>
  );
}

function DeliveryLogTab() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [channelFilter, setChannelFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20, sortBy: 'sent_at', order: 'desc' };
      if (statusFilter) params.status = statusFilter;
      if (channelFilter) params.channel = channelFilter;
      const { data } = await api.get('/notifications/log', { params });
      setLogs(data.data);
      setPagination(data.pagination);
    } catch {} finally {
      setLoading(false);
    }
  }, [page, statusFilter, channelFilter]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  return (
    <>
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="flex gap-2 flex-wrap">
          <span className="text-sm text-secondary-500 self-center mr-1">Status:</span>
          {['', 'sent', 'failed', 'delivered'].map((s) => (
            <button key={s} onClick={() => { setStatusFilter(s); setPage(1); }}
              className={`px-3 py-1.5 text-sm rounded-lg border transition ${
                statusFilter === s
                  ? 'bg-primary-50 border-primary-200 text-primary-700'
                  : 'border-secondary-200 text-secondary-600 hover:bg-secondary-50'
              }`}>
              {s || 'All'}
            </button>
          ))}
        </div>
        <div className="flex gap-2 flex-wrap">
          <span className="text-sm text-secondary-500 self-center mr-1">Channel:</span>
          {['', 'email', 'sms'].map((c) => (
            <button key={c} onClick={() => { setChannelFilter(c); setPage(1); }}
              className={`px-3 py-1.5 text-sm rounded-lg border transition ${
                channelFilter === c
                  ? 'bg-primary-50 border-primary-200 text-primary-700'
                  : 'border-secondary-200 text-secondary-600 hover:bg-secondary-50'
              }`}>
              {c || 'All'}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary-50 text-secondary-600">
              <tr>
                <th className="px-5 py-3 text-left font-medium">Channel</th>
                <th className="px-5 py-3 text-left font-medium">Recipient</th>
                <th className="px-5 py-3 text-left font-medium">Device</th>
                <th className="px-5 py-3 text-left font-medium">Template</th>
                <th className="px-5 py-3 text-left font-medium">Subject</th>
                <th className="px-5 py-3 text-left font-medium">Status</th>
                <th className="px-5 py-3 text-left font-medium">Error</th>
                <th className="px-5 py-3 text-left font-medium">Sent At</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-secondary-100">
              {loading && (
                <tr><td colSpan={8} className="px-5 py-8 text-center text-secondary-400">Loading...</td></tr>
              )}
              {!loading && logs.length === 0 && (
                <tr><td colSpan={8} className="px-5 py-8 text-center text-secondary-400">No notification logs found</td></tr>
              )}
              {!loading && logs.map((log) => (
                <tr key={log.id} className="hover:bg-secondary-50 transition">
                  <td className="px-5 py-3"><ChannelBadge channel={log.channel} /></td>
                  <td className="px-5 py-3 text-secondary-700">{log.recipient || '--'}</td>
                  <td className="px-5 py-3 text-primary-600 font-medium">{log.device_id || '--'}</td>
                  <td className="px-5 py-3 text-secondary-600 text-xs">{log.template_key || '--'}</td>
                  <td className="px-5 py-3 text-secondary-700 max-w-xs truncate">{log.subject || '--'}</td>
                  <td className="px-5 py-3"><StatusBadge status={log.status} /></td>
                  <td className="px-5 py-3 text-red-600 text-xs max-w-xs truncate">{log.error || '--'}</td>
                  <td className="px-5 py-3 text-secondary-500 text-xs whitespace-nowrap">{formatDateTime(log.sent_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {pagination.totalPages > 1 && (
          <div className="px-5 py-3 border-t border-secondary-100 flex items-center justify-between">
            <p className="text-sm text-secondary-500">Page {pagination.page} of {pagination.totalPages}</p>
            <div className="flex gap-2">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                className="px-3 py-1 text-sm rounded-lg border border-secondary-200 hover:bg-secondary-50 disabled:opacity-40">Prev</button>
              <button onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))} disabled={page >= pagination.totalPages}
                className="px-3 py-1 text-sm rounded-lg border border-secondary-200 hover:bg-secondary-50 disabled:opacity-40">Next</button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function ButtonPressesTab() {
  const [presses, setPresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [outcomeFilter, setOutcomeFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});

  const fetchPresses = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20, sortBy: 'pressed_at', order: 'desc' };
      if (outcomeFilter) params.outcome = outcomeFilter;
      const { data } = await api.get('/notifications/button-presses', { params });
      setPresses(data.data);
      setPagination(data.pagination);
    } catch {} finally {
      setLoading(false);
    }
  }, [page, outcomeFilter]);

  useEffect(() => { fetchPresses(); }, [fetchPresses]);

  return (
    <>
      {/* Filters */}
      <div className="flex gap-2 flex-wrap mb-4">
        <span className="text-sm text-secondary-500 self-center mr-1">Outcome:</span>
        {['', 'checkin_clear', 'emergency', 'notified', 'no_contact', 'no_action', 'short_press', 'long_press', 'error'].map((o) => (
          <button key={o} onClick={() => { setOutcomeFilter(o); setPage(1); }}
            className={`px-3 py-1.5 text-sm rounded-lg border transition ${
              outcomeFilter === o
                ? 'bg-primary-50 border-primary-200 text-primary-700'
                : 'border-secondary-200 text-secondary-600 hover:bg-secondary-50'
            }`}>
            {o ? o.replace(/_/g, ' ') : 'All'}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary-50 text-secondary-600">
              <tr>
                <th className="px-5 py-3 text-left font-medium">Device ID</th>
                <th className="px-5 py-3 text-left font-medium">Pressed At</th>
                <th className="px-5 py-3 text-left font-medium">Duration (s)</th>
                <th className="px-5 py-3 text-left font-medium">Outcome</th>
                <th className="px-5 py-3 text-left font-medium">Detail</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-secondary-100">
              {loading && (
                <tr><td colSpan={5} className="px-5 py-8 text-center text-secondary-400">Loading...</td></tr>
              )}
              {!loading && presses.length === 0 && (
                <tr><td colSpan={5} className="px-5 py-8 text-center text-secondary-400">No button presses found</td></tr>
              )}
              {!loading && presses.map((p) => (
                <tr key={p.id} className="hover:bg-secondary-50 transition">
                  <td className="px-5 py-3 text-primary-600 font-medium">{p.device_id}</td>
                  <td className="px-5 py-3 text-secondary-500 text-xs whitespace-nowrap">{formatDateTime(p.pressed_at)}</td>
                  <td className="px-5 py-3 text-secondary-700">{p.duration_seconds != null ? p.duration_seconds.toFixed(1) : '--'}</td>
                  <td className="px-5 py-3"><OutcomeBadge outcome={p.outcome} /></td>
                  <td className="px-5 py-3 text-secondary-600 max-w-xs truncate">{p.detail || '--'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {pagination.totalPages > 1 && (
          <div className="px-5 py-3 border-t border-secondary-100 flex items-center justify-between">
            <p className="text-sm text-secondary-500">Page {pagination.page} of {pagination.totalPages}</p>
            <div className="flex gap-2">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                className="px-3 py-1 text-sm rounded-lg border border-secondary-200 hover:bg-secondary-50 disabled:opacity-40">Prev</button>
              <button onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))} disabled={page >= pagination.totalPages}
                className="px-3 py-1 text-sm rounded-lg border border-secondary-200 hover:bg-secondary-50 disabled:opacity-40">Next</button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

// Inbound SMS audit trail: every reply to a CheckInCare text (check-in "1",
// STOP/START, HELP, unmatched) with its disposition and the exact reply sent.
function SmsRepliesTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (actionFilter) params.action = actionFilter;
      const { data } = await api.get('/notifications/sms-inbound', { params });
      setRows(data.data);
      setPagination(data.pagination);
    } catch {} finally {
      setLoading(false);
    }
  }, [page, actionFilter]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  return (
    <>
      {/* Filters */}
      <div className="flex gap-2 flex-wrap mb-4">
        <span className="text-sm text-secondary-500 self-center mr-1">Action:</span>
        {['', 'checkin_reset', 'checkin_duplicate', 'opt_out', 'opt_in', 'help', 'no_pending', 'no_match', 'unknown', 'error'].map((a) => (
          <button key={a} onClick={() => { setActionFilter(a); setPage(1); }}
            className={`px-3 py-1.5 text-sm rounded-lg border transition ${
              actionFilter === a
                ? 'bg-primary-50 border-primary-200 text-primary-700'
                : 'border-secondary-200 text-secondary-600 hover:bg-secondary-50'
            }`}>
            {a ? a.replace(/_/g, ' ') : 'All'}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary-50 text-secondary-600">
              <tr>
                <th className="px-5 py-3 text-left font-medium">Received At</th>
                <th className="px-5 py-3 text-left font-medium">From</th>
                <th className="px-5 py-3 text-left font-medium">Message</th>
                <th className="px-5 py-3 text-left font-medium">Action</th>
                <th className="px-5 py-3 text-left font-medium">Device</th>
                <th className="px-5 py-3 text-left font-medium">Reply Sent</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-secondary-100">
              {loading && (
                <tr><td colSpan={6} className="px-5 py-8 text-center text-secondary-400">Loading...</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={6} className="px-5 py-8 text-center text-secondary-400">No inbound SMS recorded</td></tr>
              )}
              {!loading && rows.map((r) => (
                <tr key={r.id} className="hover:bg-secondary-50 transition">
                  <td className="px-5 py-3 text-secondary-500 text-xs whitespace-nowrap">{formatDateTime(r.received_at)}</td>
                  <td className="px-5 py-3 text-secondary-700 whitespace-nowrap">{r.from_number || '--'}</td>
                  <td className="px-5 py-3 text-secondary-700 max-w-[10rem] truncate" title={r.body || ''}>{r.body || '--'}</td>
                  <td className="px-5 py-3"><SmsActionBadge action={r.action} /></td>
                  <td className="px-5 py-3 text-primary-600 font-medium whitespace-nowrap">{r.matched_device_id || '--'}</td>
                  <td className="px-5 py-3 text-secondary-600 max-w-xs truncate" title={r.reply_sent || ''}>{r.reply_sent || '--'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {pagination.totalPages > 1 && (
          <div className="px-5 py-3 border-t border-secondary-100 flex items-center justify-between">
            <p className="text-sm text-secondary-500">Page {pagination.page} of {pagination.totalPages}</p>
            <div className="flex gap-2">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                className="px-3 py-1 text-sm rounded-lg border border-secondary-200 hover:bg-secondary-50 disabled:opacity-40">Prev</button>
              <button onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))} disabled={page >= pagination.totalPages}
                className="px-3 py-1 text-sm rounded-lg border border-secondary-200 hover:bg-secondary-50 disabled:opacity-40">Next</button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default function NotificationLog() {
  const [activeTab, setActiveTab] = useState('delivery');

  const tabs = [
    { key: 'delivery', label: 'Delivery Log' },
    { key: 'presses', label: 'Button Presses' },
    { key: 'sms', label: 'SMS Replies' },
  ];

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Notification Log</h1>
          <p className="text-secondary-500 text-sm mt-1">View notification delivery history, button press events, and inbound SMS replies</p>
        </div>
      </div>

      {/* Tab buttons */}
      <div className="flex gap-2">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`px-4 py-2 text-sm font-medium rounded-lg border transition ${
              activeTab === t.key
                ? 'bg-primary-600 border-primary-600 text-white'
                : 'border-secondary-200 text-secondary-600 hover:bg-secondary-50'
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'delivery' && <DeliveryLogTab />}
      {activeTab === 'presses' && <ButtonPressesTab />}
      {activeTab === 'sms' && <SmsRepliesTab />}
    </div>
  );
}
